/**
 * Thin Chat module HTTP client for the Connect shell DM panel.
 */

import { buildAuthHeaders, getApiOrigin } from '@/auth';

export interface ChatAccount {
  id: number;
  uuid: string;
  name: string;
  email: string;
  active: boolean;
}

export interface ChatChannelMember {
  id?: number;
  accountId?: number;
  account_id?: number;
  account?: { id?: number; name?: string; email?: string; address?: string };
}

export interface ChatChannel {
  id: number;
  type: string;
  name?: string | null;
  members?: ChatChannelMember[];
}

export interface ChatMessage {
  id: number;
  channelId?: number;
  senderAccountId?: number;
  sender_account_id?: number;
  content: string;
  createdAt?: string;
  created_at?: string;
  senderName?: string;
}

function chatUrl(path: string): string {
  const base = `${getApiOrigin()}/v1`.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

async function chatFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = buildAuthHeaders({
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'BLP-Module': 'chat',
  });
  const res = await fetch(chatUrl(path), {
    ...init,
    headers: {
      ...Object.fromEntries(headers.entries()),
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    let message = `chat ${res.status}`;
    try {
      // A refusal is `{"error": "<text>"}`, a typed answer `{code, message}`.
      const body = (await res.json()) as { error?: string; message?: string };
      message = body?.error || body?.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** The active accounts of the tenant, read to their end: the endpoint answers one page. */
export async function listChatAccounts(): Promise<ChatAccount[]> {
  const out: ChatAccount[] = [];
  for (let page = 1; ; page += 1) {
    const data = await chatFetch<{
      accounts?: ChatAccount[];
      metadata?: { pages?: number };
    }>(`/chat/accounts?perPage=200&page=${page}`);
    const rows = data.accounts ?? [];
    out.push(...rows);
    const pages = Number(data.metadata?.pages);
    if (rows.length === 0 || !Number.isFinite(pages) || page >= pages) break;
  }
  return out;
}

export async function listChannels(): Promise<ChatChannel[]> {
  const data = await chatFetch<{ channels?: ChatChannel[] }>('/channels');
  return data.channels ?? [];
}

/**
 * Opens the direct message with another account. It exists once per pair of
 * accounts, which the server guarantees: it answers the pair's channel (200)
 * or a new one (201), either way with its two members.
 */
export async function openDirectChannel(
  peerAccountId: number,
): Promise<ChatChannel> {
  const data = await chatFetch<ChatChannel>('/channels', {
    method: 'POST',
    body: JSON.stringify({
      type: 'direct',
      members: [{ accountId: peerAccountId }],
    }),
  });
  if (!data?.id) throw new Error('invalid channel response');
  return data;
}

export async function listMessages(channelId: number): Promise<ChatMessage[]> {
  const data = await chatFetch<{ messages?: ChatMessage[] }>(
    `/channels/${channelId}/messages?perPage=50`,
  );
  // The API answers the newest page oldest first, as the panel shows it.
  return data.messages ?? [];
}

export async function sendMessage(
  channelId: number,
  content: string,
  senderAccountId: number,
): Promise<ChatMessage> {
  const data = await chatFetch<ChatMessage>(`/channels/${channelId}/messages`, {
    method: 'POST',
    body: JSON.stringify({
      content,
      type: 'text',
      senderAccountId,
    }),
  });
  if (!data?.id) throw new Error('invalid message response');
  return data;
}
